import React, { useState, useCallback, useEffect, Fragment } from "react";
import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
    DebouncedInput,
    IndeterminateCheckbox,
    StatusFilter,
} from "../Common/Filters";
import { ArrowPathIcon, FunnelIcon } from "@heroicons/react/24/outline";
import ProcessingLoader from "./ProcessingLoader";
import FetchingDataLoader from "./FetchingDataLoader";
import ConfirmationPopup from "./ConfirmationPopup";
import Popup from "./Popup";
import { Menu, Transition } from "@headlessui/react";
import { formattedDate } from "./CommonUtils";
import IndexBasedRangeSelection from "./IndexBasedRangeSelection";
import axios from "axios";
import { PROJECT_URL } from "../../imports";
import { handleApiError } from "./APIUtils";

export default function FilesToProcess({
    items=[],
    setFilesData,
    startProcess,
    ReloadData,
    serverStatus,
    Loader,
    setProcessOption,
    backgroundProcess,
    setServerOFF,
    startIndex,
    endIndex,
    setStartIndex,
    setEndIndex,
    rowSelection,
    setRowSelection,
    hideQdbColumn = false,
    process_code = ""
}) {
    let projectState = sessionStorage.getItem("projectStatus");
    let [isOpenPopup, setIsOpenPopup] = useState(false);
    let [msg, setMsg] = useState(false);
    let [isSuccess, setIsSuccess] = useState(false);
    const [checked, setChecked] = useState(false);
    const [checkVisible, setCheckVisible] = useState(false);

    function closePopup() {
        setIsOpenPopup(false);
    }

    const openPopup = useCallback(() => {
        setIsOpenPopup(true);
    }, [setIsOpenPopup]);

    const [isOpenConfirmPopup, setIsOpenConfirmPopup] = useState(false);
    const [confirmationMsg, setConfirmationMsg] = useState("");

    function closeConfirmPopup() {
        setIsOpenConfirmPopup(false);
    }

    function openConfirmPopup(confirmationmsgprop) {
        setConfirmationMsg(confirmationmsgprop);
        setIsOpenConfirmPopup(true);
    }

    const handleSelectedState = () => {
        setIsOpenConfirmPopup(false);
        setServerOFF(checked);
        // Filter the Files based on the rowSelection
        const selectedFiles = Object.keys(rowSelection)
            .filter((index) => rowSelection[index])
            .map((index) => data[index]);
        setFilesData(selectedFiles);
    };

    const [globalFilter, setGlobalFilter] = React.useState("");
    // const [sorting, setSorting] = React.useState([]);
    const [sorting, setSorting] = React.useState([{ id: "filename", desc: false }]);
  
    // Update rowSelection based on start and end index inputs
    const handleSetSelection = () => {
      if (startIndex !== null && endIndex !== null && startIndex <= endIndex) {
        const newSelection = {};
        for (let i = startIndex-1; i <= endIndex-1; i++) {
          newSelection[i] = true;  // Mark these indexes as selected
        }
        setRowSelection(newSelection);
      }
    };

    const finalizedSortFn = (rowA, rowB) => {
        const a = rowA.original;
        const b = rowB.original;

        const isAFinalized = (a.filename || "").includes("_finalizedfromPP");
        const isBFinalized = (b.filename || "").includes("_finalizedfromPP");

        // Get the file_code to pair originals with their finalized counterpart
        const codeA = a.file_code || "";
        const codeB = b.file_code || "";

        // If same file_code: original always before finalized
        if (codeA === codeB) {
            return isAFinalized ? 1 : -1;
        }

        // Both are finalized — sort by their file_code (proxy for upload order)
        if (isAFinalized && isBFinalized) {
            return codeA.localeCompare(codeB);
        }

        // Both are original — sort by their file_code
        if (!isAFinalized && !isBFinalized) {
            return codeA.localeCompare(codeB);
        }

        // One is finalized, one is original with different file_code
        // Find the finalized one's pair among originals and sort accordingly
        return codeA.localeCompare(codeB);
    };

    const columns = React.useMemo(
        () => [
            {
                id: "select",
                size: 10,
                header: ({ table }) => (
                    <IndeterminateCheckbox
                        {...{
                            checked: table.getIsAllRowsSelected(),
                            indeterminate: table.getIsSomeRowsSelected(),
                            onChange: table.getToggleAllRowsSelectedHandler(),
                        }}
                    />
                ),
                cell: ({ row }) => (
                    <center className="px-1">
                        <IndeterminateCheckbox
                            {...{
                                checked: row.getIsSelected(),
                                disabled: !row.getCanSelect(),
                                indeterminate: row.getIsSomeSelected(),
                                onChange: row.getToggleSelectedHandler(),
                            }}
                        />
                    </center>
                ),
            },
            {
                accessorKey: "index",
                enableColumnFilter: false,
                enableSorting: false,
                size: 50,
                header: "#",
                cell: ({ row }) => <center>{row.index + 1}</center>,
            },
            {
                accessorKey: "filename",
                enableColumnFilter: false,
                size: 1000,
                header: () => <span>File Name</span>,
                sortingFn: finalizedSortFn,
            },
            // {
            //     accessorKey: "qdb_code",
            //     enableColumnFilter: false,
            //     size: 250,
            //     header: () => <span>QDB</span>,
            // },
            // ...(hideQdbColumn ? [] : [
            // {
            //     accessorKey: "qdb",
            //     header: "QDB",
            //     cell: ({ row }) => {
            //         return (
            //         <span>
            //             {row.original.qdb ||
            //             row.original.qdb_code}
            //         </span>
            //         );
            //     }
            // }] : []),
            ...(!hideQdbColumn ? [{
                accessorKey: "qdb",
                header: "QDB",
                cell: ({ row }) => {
                    return (
                    <span>
                        {row.original.qdb ||
                        row.original.qdb_code}
                    </span>
                    );
                }
            }] : []),

            // {
            //     accessorKey: "status",
            //     enableColumnFilter: true,
            //     filterFn: "equals",
            //     size: 50,
            //     header: () => <span>Status</span>,
            //     cell: ({ row }) => (
            //         <div className="flex justify-center">
            //             <div
            //                 className={`${(row.original.status === "success" || row.original.status === "SUCCESS") &&
            //                     "bg-green-500"
            //                     } ${row.original.status === "error" && "bg-red-500"
            //                     } ${row.original.status === "Unprocessed" &&
            //                     "bg-yellow-500"
            //                     }  ${row.original.status !== "success" &&
            //                     row.original.status !== "error" &&
            //                     row.original.status !== "Unprocessed" &&
            //                     "bg-yellow-500"
            //                     } 
            //                 text-white text-xs w-fit px-2 py-1 font-bold rounded-xl flex flex-nowrap`}
            //             >
            //                 {row.original.status && row.original.status}
            //                 {!row.original.status && "Unprocessed"}
            //             </div>
            //         </div>
            //     ),
            // },
            {
                accessorKey: "status",
                enableColumnFilter: true,
                filterFn: "equals",
                size: 50,
                header: () => <span>Status</span>,
                cell: ({ row }) => {
                    // const status = row.original.status?.toLowerCase();
                    const status = String(row.original.status || "").toLowerCase();

                    return (
                    <div className="flex justify-center">
                        <div
                        className={`
                            ${status === "success" && "bg-green-500"}
                            ${(status?.toLowerCase() === "error" || status?.toLowerCase() === "failed") && "bg-red-500"}
                            ${status === "unprocessed" && "bg-yellow-500"}
                            ${!["success", "error", "failed", "unprocessed"].includes(status) && "bg-yellow-500"}
                            text-white text-xs w-fit px-2 py-1 font-bold rounded-xl flex flex-nowrap
                        `}
                        >
                        {row.original.status || "Unprocessed"}
                        </div>
                    </div>
                    );
                },
            },
            {
                accessorKey: "param_code",
                enableColumnFilter: false,
                size: 50,
                header: () => <span>Param</span>,
            },
            {
                // accessorKey: "user_id",
                accessorKey: "processed_by",
                enableColumnFilter: false,
                size: 50,
                header: () => <span>Processed By</span>,
                cell: ({ row }) => (
                    <span className="capitalize">{row.original.processed_by}</span>
                ),
            },
            {
                // accessorKey: "creation_date",
                accessorKey: "execution_date",
                enableColumnFilter: false,
                size: 100,
                header: "Execution Date",
                cell: ({ row }) => (
                    <div>
                        {row.original.execution_date !== "" &&
                            formattedDate(row.original.execution_date)}
                    </div>
                ),
            },
        ],
        [hideQdbColumn]
    );

    const [data, setData] = useState([]);

    // useEffect(() => {
    //     setData(items);
    // }, [items]);

    useEffect(() => {
        // Add safety check before setting data
        if (items && Array.isArray(items)) {
            setData(items);
        } else {
            setData([]);
        }
    }, [items]);

    const table = useReactTable({
        data,
        columns,
        state: {
            rowSelection,
            sorting,
            globalFilter,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const renderTableCellIndex = (row) => {
        return (
            table.getFilteredRowModel().rows.indexOf(row) + 1 ||
            table.getSortedRowModel().rows.indexOf(row) + 1 ||
            row.index + 1
        );
    };

    // const tableContainerRef = React.useRef(null);

    // const handleSelectedProcess = async(option) => {
    //     // Filter the Files based on the rowSelection
    //     const selectedFiles = Object.keys(rowSelection)
    //         .filter((index) => rowSelection[index])
    //         .map((index) => data[index]);
    //         if (selectedFiles.length === 0) {
    //             setMsg("Please select files to process");
    //             setIsSuccess(false);
    //             openPopup();
    //             return;
    //         }

    //         // Check if refined answer param is active
    //         try {
    //             const paramResponse = await axios.get(
    //                 `${PROJECT_URL}/projects/params/${sessionStorage.getItem("projectCode")}`,
    //                 {
    //                     headers: {
    //                         Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    //                     },
    //                 }
    //             );
                
    //             const hasActiveRefinedParam = paramResponse.data.data.some(
    //                 param => (param.process_code === 'REFINED' || param.process_code === 'Refined Answers') && param.status === true
    //             );
                
    //             if (!hasActiveRefinedParam) {
    //                 setMsg("Refined answer parameter is not configured or active. Please configure it first.");
    //                 setIsSuccess(false);
    //                 openPopup();
    //                 return;
    //             }
    //         } catch (error) {
    //             const errorMessage = handleApiError(error);
    //             setMsg("Failed to verify parameter configuration");
    //             setIsSuccess(false);
    //             openPopup();
    //             return;
    //         }
    //     if (option === "background" && selectedFiles.length > 0) {
    //         setCheckVisible(true);
    //         openConfirmPopup(`Are you sure that you want to process ${selectedFiles.length} files via background?`);
    //         setProcessOption("background");
    //     } else if (selectedFiles.length > 0 && selectedFiles.length <= 5) {
    //         setCheckVisible(false);
    //         setFilesData(selectedFiles);
    //         setProcessOption("normal");
    //     } else if (selectedFiles.length > 5 && selectedFiles.length !== data.length) {
    //         setCheckVisible(false);
    //         openConfirmPopup("Are you sure that you want to process more than 5 files?");
    //         setProcessOption("normal");
    //     } else if (selectedFiles.length === data.length) {
    //         setCheckVisible(false);
    //         openConfirmPopup("Are you sure that you want to process all the files?");
    //         setProcessOption("normal");
    //     } else {
    //         setMsg("Please select files to process");
    //         setIsSuccess(false);
    //         openPopup();
    //     }
    // };

    const handleSelectedProcess = async (option) => {
        console.log("process_code", process_code);
        const selectedFiles = Object.keys(rowSelection)
            .filter((index) => rowSelection[index])
            .map((index) => data[index]);
        
        if (selectedFiles.length === 0) {
            setMsg("Please select files to process");
            setIsSuccess(false);
            openPopup();
            return;
        }

        // Check for active param based on processCode
        if (process_code) {
            try {
                const paramResponse = await axios.get(
                    `${PROJECT_URL}/projects/params/${sessionStorage.getItem("projectCode")}`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                        },
                    }
                );
                console.log("paramResponse", paramResponse.data.data);
                console.log("Looking for process_code", process_code);
                
                const hasActiveParam = paramResponse.data.data.some(
                //     param => (param.process_code === 'REFINED' || param.process_code === 'Refined Answers') && param.status === true
                // );
                //     param => (param.process_code === process_code || param.process_code === process_code.replace('_', ' ')) && param.status === true
                // );
                param => {
                    console.log("Checking param:", param.process_code, "status:", param.status);
                    return param.process_code === process_code && param.status === true;
                });
                console.log("hasActiveParam:", hasActiveParam);
                
                if (!hasActiveParam) {
                    // const paramName = process_code === 'RAW' ? 'Raw answer' : 'Refined answer';
                    const paramName = process_code === 'RAW' ? 'Raw answer' : 
                    process_code === 'GETIDEAS' ? 'Get Ideas' : 'Refined answer';
                    setMsg(`${paramName} parameter is not configured or active. Please configure it first.`);
                    setIsSuccess(false);
                    openPopup();
                    return;
                }
            } catch (error) {
                setMsg("Failed to verify parameter configuration");
                setIsSuccess(false);
                openPopup();
                return;
            }
        }

        if (option === "background" && selectedFiles.length > 0) {
            setCheckVisible(true);
            openConfirmPopup(`Are you sure that you want to process ${selectedFiles.length} files via background?`);
            setProcessOption("background");
        } else if (selectedFiles.length > 0 && selectedFiles.length <= 5) {
            setCheckVisible(false);
            setFilesData(selectedFiles);
            setProcessOption("normal");
        } else if (selectedFiles.length > 5 && selectedFiles.length !== data.length) {
            setCheckVisible(false);
            openConfirmPopup("Are you sure that you want to process more than 5 files?");
            setProcessOption("normal");
        } else if (selectedFiles.length === data.length) {
            setCheckVisible(false);
            openConfirmPopup("Are you sure that you want to process all the files?");
            setProcessOption("normal");
        }
    };

    // const { rows } = table.getRowModel();
    // const rowVirtualizer = useVirtualizer({
    //     parentRef: tableContainerRef,
    //     size: rows.length,
    //     overscan: data.length,
    // });
    // const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

    const tableContainerRef = React.useRef(null);

    const { rows } = table.getRowModel();

    // Only initialize virtualizer when ref is ready and we have data
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 50, // Estimated row height in pixels
        overscan: 10, // Number of items to render outside visible area
        // enabled: rows.length > 0,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();
    const safeVirtualRows = virtualItems || []; 

    // const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

    const paddingTop = safeVirtualRows.length > 0 ? safeVirtualRows[0]?.start || 0 : 0;

    // const paddingBottom =
    //     safeVirtualRows.length > 0
    //         ? totalSize - (safeVirtualRows[safeVirtualRows.length - 1]?.end || 0)
    //         : 0;

    const paddingBottom = 
        safeVirtualRows.length > 0 
        ? rowVirtualizer.getTotalSize() - (safeVirtualRows[safeVirtualRows.length - 1]?.end || 0)
        : 0;

    // const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
    // const paddingBottom =
    //     virtualRows.length > 0
    //         ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
    //         : 0;

    // const safeVirtualRows = virtualRows || [];

    // const paddingTop = safeVirtualRows.length > 0 ? safeVirtualRows[0]?.start || 0 : 0;

    // const paddingBottom =
    //     safeVirtualRows.length > 0
    //         ? totalSize - (safeVirtualRows[safeVirtualRows.length - 1]?.end || 0)
    //         : 0;

    console.log("Rows:", rows.length, "Virtual rows:", safeVirtualRows.length);

    useEffect(() => {
        console.log("Items received in FilesToProcess:", items);
        if (items && Array.isArray(items)) {
            console.log("Setting data to:", items);
            setData(items);
        } else {
            setData([]);
        }
    }, [items]);

    console.log("serverStatus:", serverStatus);
    console.log("projectState:", projectState);
    console.log("rows.length:", rows.length);


    return (
        <div className="m-2 border rounded-lg bg-white font-GoogleSans tracking-wider overflow-hidden">
            <ConfirmationPopup
                isOpenConfirmPopup={isOpenConfirmPopup}
                confirmationMsg={confirmationMsg}
                closeConfirmPopup={closeConfirmPopup}
                onStateChange={handleSelectedState}
                checkVisible={checkVisible}
                checkState={checked}
                onCheckChange={(e)=>setChecked(e.target.checked)}
            />
            <Popup
                isOpen={isOpenPopup}
                msg={msg}
                closeModal={closePopup}
                isSuccess={isSuccess}
            />
            <div className="flex flex-wrap justify-between m-3 gap-2">
                <DebouncedInput
                    value={globalFilter ?? ""}
                    onChange={(value) => setGlobalFilter(String(value))}
                    placeholder="Search..."
                    wclass="w-full md:w-auto 2xl:w-[20rem]"
                />
                <div className="flex flex-wrap gap-2">
                    <IndexBasedRangeSelection startIndex={startIndex} setStartIndex={setStartIndex} endIndex={endIndex} setEndIndex={setEndIndex} handleSetSelection={handleSetSelection} max={items?.length || 0} />
                    <button
                        className="rounded-md cursor-default lg:cursor-pointer p-2 inline-flex text-sm border-gray-300 border"
                        onClick={ReloadData}
                    >
                        <ArrowPathIcon className="h-5 text-green-700" />
                    </button>
                    <Menu as="div" className="relative">
                        <div>
                            <Menu.Button className="relative justify-center border border-gray-300 rounded-md p-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer">
                                <span className="absolute -inset-1.5" />
                                <FunnelIcon className="h-5 w-auto" />
                            </Menu.Button>
                        </div>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="fixed right-8 z-30 mt-3 w-fit px-2 pb-2 space-y-3 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div>
                                    {data.length > 0 &&
                                        table
                                            .getHeaderGroups()
                                            .map((headerGroup) => (
                                                <div
                                                    key={headerGroup.id}
                                                    className="space-y-1"
                                                >
                                                    {headerGroup.headers.map(
                                                        (header) => (
                                                            <div
                                                                key={header.id}
                                                            >
                                                                {header.column.getCanFilter() &&
                                                                    header.column
                                                                        .id ===
                                                                    "status" ? (
                                                                    <div>
                                                                        <label
                                                                            htmlFor="push-everything"
                                                                            className="block text-xs font-bold tracking-widest leading-6 text-gray-400 ps-1"
                                                                        >
                                                                            Status
                                                                        </label>
                                                                        <StatusFilter
                                                                            column={
                                                                                header.column
                                                                            }
                                                                            table={
                                                                                table
                                                                            }
                                                                        />
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            ))}
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            </div>
            {Loader && <FetchingDataLoader />}
            {!Loader && (
                <>
                    <div
                        ref={tableContainerRef}
                        className="overflow-auto resp-processtable-h border-t"
                    >
                        <table
                            key={data.length}
                            className={`flex-inline w-full border-collapse ${startProcess === true && "animate-pulse"
                                }`}
                        >
                            <thead className="text-sm lg:text-base bg-gray-200">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <th
                                                    className="border p-2 whitespace-nowrap"
                                                    key={header.id}
                                                    colSpan={header.colSpan}
                                                    style={{
                                                        width: header.getSize(),
                                                    }}
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <>
                                                            <div
                                                                className={`${header.column.getCanSort()
                                                                        ? "cursor-pointer select-none justify-start"
                                                                        : "justify-center"
                                                                    } flex`}
                                                                onClick={header.column.getToggleSortingHandler()}
                                                            >
                                                                {flexRender(
                                                                    header
                                                                        .column
                                                                        .columnDef
                                                                        .header,
                                                                    header.getContext()
                                                                )}
                                                                {/* {{
                                                                    asc: " 🔼",
                                                                    desc: " 🔽",
                                                                }[
                                                                    header.column.getIsSorted()
                                                                ] || null} */}
                                                                {header.column.id !== "filename" && 
                                                                    ({
                                                                        asc: " 🔼",
                                                                        desc: " 🔽",
                                                                     }[header.column.getIsSorted()] || null
                                                                    )
                                                                }
                                                            </div>
                                                        </>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {
                                    rows.length === 0 ?
                                        (
                                            <tr>
                                                <td className='border p-2' colSpan={table.getHeaderGroups()[0].headers.length} style={{ textAlign: 'center' }}>
                                                    No Data Found
                                                </td>
                                            </tr>
                                        )
                                        :
                                        (
                                            <>
                                                {paddingTop > 0 && (
                                                    <tr>
                                                        <td
                                                            className="border p-2"
                                                            style={{
                                                                height: `${paddingTop}px`,
                                                            }}
                                                        />
                                                    </tr>
                                                )}
                                                {/* {virtualRows.map((virtualRow) => { */}
                                                {safeVirtualRows.map((virtualRow) => {
                                                    const row = rows[virtualRow.index];
                                                    return (
                                                        <tr
                                                            key={row.id}
                                                            className="text-sm bg-white transition duration-300 ease-in-out hover:bg-gray-100"
                                                        >
                                                            {row
                                                                .getVisibleCells()
                                                                .map((cell) => {
                                                                    return (
                                                                        <td
                                                                            className="border p-2 whitespace-nowrap"
                                                                            key={cell.id}
                                                                        >
                                                                            {cell.column.id === "index" && <center>{renderTableCellIndex(row)}</center>}
                                                                            {cell.column.id !==
                                                                                "index" &&
                                                                                flexRender(
                                                                                    cell.column
                                                                                        .columnDef
                                                                                        .cell,
                                                                                    cell.getContext()
                                                                                )}
                                                                        </td>
                                                                    );
                                                                })}
                                                        </tr>
                                                    );
                                                })}
                                                {paddingBottom > 0 && (
                                                    <tr>
                                                        <td
                                                            className="border p-2"
                                                            style={{
                                                                height: `${paddingBottom}px`,
                                                            }}
                                                        />
                                                    </tr>
                                                )}
                                            </>
                                        )
                                }
                            </tbody>
                        </table>
                    </div>
                    {serverStatus && projectState === "active" && (
                        <div className="flex justify-end lg:justify-start border-t gap-1 p-3">
                            {!startProcess && (
                                <button
                                    type="button"
                                    disabled={startProcess || backgroundProcess}
                                    onClick={() =>
                                        handleSelectedProcess("normal")
                                    }
                                    className="inline-flex justify-center rounded-lg border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer"
                                >
                                    Start Process
                                </button>
                            )}
                            {/* {!backgroundProcess && (
                                <button
                                    type="button"
                                    disabled={startProcess || backgroundProcess}
                                    onClick={() =>
                                        handleSelectedProcess("background")
                                    }
                                    className="inline-flex justify-center rounded-lg border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer"
                                >
                                    Start Background Process
                                </button>
                            )} */}
                            {(startProcess || backgroundProcess) && (
                                <ProcessingLoader />
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
